import React, { useState, useEffect, useCallback } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
  FaCommentDots,
  FaStar,
  FaPaperPlane,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import ReusableModal from "./ReusableModal";
import { submitFeedback } from "../api/overviewApi";

const MAX_MESSAGE_LENGTH = 2000;

const CATEGORY_OPTIONS = [
  { value: "", label: "Select a category..." },
  { value: "general", label: "General Feedback" },
  { value: "bug", label: "Bug Report" },
  { value: "feature", label: "Feature Request" },
  { value: "ui_ux", label: "UI / UX Improvement" },
  { value: "performance", label: "Performance Issue" },
  { value: "other", label: "Other" },
];

const INITIAL_FORM = {
  category: "",
  rating: 0,
  message: "",
};

const FeedbackModal = ({ open, onClose, userEmail = "", clientName = "" }) => {
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [hoverRating, setHoverRating] = useState(0);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ ...INITIAL_FORM });
      setHoverRating(0);
      setErrors({});
      setTouched({});
      setSubmitResult(null);
      setSubmitting(false);
    }
  }, [open]);

  const validate = useCallback((formData) => {
    const errs = {};
    if (!formData.category) {
      errs.category = "Please select a feedback category.";
    }
    if (!formData.message.trim()) {
      errs.message = "Feedback message is required.";
    } else if (formData.message.trim().length < 10) {
      errs.message = "Please provide at least 10 characters.";
    } else if (formData.message.trim().length > MAX_MESSAGE_LENGTH) {
      errs.message = `Message must be ${MAX_MESSAGE_LENGTH} characters or fewer.`;
    }
    return errs;
  }, []);

  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      const allErrors = validate(form);
      const visibleErrors = {};
      Object.keys(touched).forEach((key) => {
        if (allErrors[key]) visibleErrors[key] = allErrors[key];
      });
      setErrors(visibleErrors);
    }
  }, [form, touched, validate]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    setSubmitResult(null);
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleStarClick = (rating) => {
    setForm((prev) => ({ ...prev, rating }));
    setSubmitResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ category: true, message: true });

    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setSubmitResult(null);

    try {
      const result = await submitFeedback({
        category: form.category,
        rating: form.rating || null,
        message: form.message,
        page_url: window.location.pathname,
      });

      setSubmitResult({
        type: "success",
        message: result.message || "Thank you! Your feedback has been submitted successfully.",
      });

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      const serverErrors = err.response?.data?.errors;
      if (serverErrors && typeof serverErrors === "object") {
        setErrors(serverErrors);
      }
      setSubmitResult({
        type: "error",
        message: err.response?.data?.error || "Failed to submit feedback. Please try again later.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = (field) => ({
    background: "var(--color-bg-card)",
    border: `1px solid ${errors[field] ? "#dc3545" : "var(--color-border)"}`,
    borderRadius: 8,
    color: "var(--color-text)",
    fontSize: 14,
    padding: "10px 14px",
    transition: "border-color 0.2s ease",
  });

  const labelStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--color-text)",
    marginBottom: 4,
  };

  const errorTextStyle = {
    fontSize: 12,
    color: "#dc3545",
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 4,
  };

  const charCount = form.message.length;
  const charCountColor =
    charCount > MAX_MESSAGE_LENGTH * 0.9 ? "#dc3545" : "var(--color-text-light)";

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Give Feedback"
      subtitle="We value your input — help us improve your experience"
      icon={FaCommentDots}
      iconColor="#17a2b8"
      iconBg="rgba(23,162,184,0.15)"
      width="min(95vw, 540px)"
      maxHeight="85vh"
    >
      <div style={{ padding: "20px 24px" }}>
        {/* Success / Error banner */}
        {submitResult && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: 8,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 10,
              background:
                submitResult.type === "success"
                  ? "rgba(40,167,69,0.12)"
                  : "rgba(220,53,69,0.12)",
              border: `1px solid ${
                submitResult.type === "success"
                  ? "rgba(40,167,69,0.3)"
                  : "rgba(220,53,69,0.3)"
              }`,
              color:
                submitResult.type === "success" ? "#28a745" : "#dc3545",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {submitResult.type === "success" ? (
              <FaCheckCircle size={16} />
            ) : (
              <FaExclamationTriangle size={16} />
            )}
            {submitResult.message}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Category */}
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Category <span style={{ color: "#dc3545" }}>*</span>
            </Form.Label>
            <Form.Select
              value={form.category}
              onChange={handleChange("category")}
              onBlur={handleBlur("category")}
              style={{
                ...inputStyle("category"),
                cursor: "pointer",
              }}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            {errors.category && (
              <div style={errorTextStyle}>
                <FaExclamationTriangle size={10} /> {errors.category}
              </div>
            )}
          </Form.Group>

          {/* Star Rating */}
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              How would you rate your experience?
            </Form.Label>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              {[1, 2, 3, 4, 5].map((star) => {
                const active = star <= (hoverRating || form.rating);
                return (
                  <FaStar
                    key={star}
                    size={28}
                    color={active ? "#f59e0b" : "var(--color-border)"}
                    style={{
                      cursor: "pointer",
                      transition: "color 0.15s, transform 0.15s",
                      transform: active ? "scale(1.15)" : "scale(1)",
                    }}
                    onClick={() => handleStarClick(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                  />
                );
              })}
              {form.rating > 0 && (
                <span
                  style={{
                    fontSize: 13,
                    color: "var(--color-text-light)",
                    marginLeft: 8,
                    alignSelf: "center",
                  }}
                >
                  {
                    ["", "Poor", "Fair", "Good", "Very Good", "Excellent"][
                      form.rating
                    ]
                  }
                </span>
              )}
            </div>
          </Form.Group>

          {/* Message */}
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Your Feedback <span style={{ color: "#dc3545" }}>*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              placeholder="Tell us what's on your mind — what went well, what could be better, or any ideas you have..."
              value={form.message}
              onChange={handleChange("message")}
              onBlur={handleBlur("message")}
              style={{
                ...inputStyle("message"),
                resize: "vertical",
                minHeight: 120,
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 4,
              }}
            >
              {errors.message ? (
                <div style={errorTextStyle}>
                  <FaExclamationTriangle size={10} /> {errors.message}
                </div>
              ) : (
                <div />
              )}
              <span style={{ fontSize: 11, color: charCountColor }}>
                {charCount}/{MAX_MESSAGE_LENGTH}
              </span>
            </div>
          </Form.Group>

          {/* Submit */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 8,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: "10px 20px",
                borderRadius: 8,
                border: "1px solid var(--color-border)",
                background: "var(--color-bg)",
                color: "var(--color-text)",
                fontWeight: 600,
                fontSize: 14,
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: "10px 24px",
                borderRadius: 8,
                border: "none",
                background: submitting
                  ? "rgba(23,162,184,0.5)"
                  : "linear-gradient(135deg, #17a2b8 0%, #138496 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 14,
                cursor: submitting ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                boxShadow: "0 2px 8px rgba(23,162,184,0.3)",
                transition: "all 0.2s ease",
              }}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" /> Submitting...
                </>
              ) : (
                <>
                  <FaPaperPlane size={13} /> Submit Feedback
                </>
              )}
            </button>
          </div>
        </Form>
      </div>
    </ReusableModal>
  );
};

export default FeedbackModal;
